import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { shallowCompare } from '../utility/functions';

const Wrapper = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    height: auto;
    // overflow-x: hidden;
    margin-bottom: 1em;
`

function stringifyAndLogMatrix(matrix) {
    const matrixString = JSON.stringify(matrix);
    const rows = matrixString.split('],');
    const formattedRows = rows.map(row => {
        const cleanedRow = row.replace('[', '').replace(']', '').trim();
        const elements = cleanedRow.split(',');
        const trimmedElements = elements.map(element => element.trim());
        const formattedRow = trimmedElements.join(' ');
        return formattedRow;
    });
    let formattedMatrix = formattedRows.join('\n');
    formattedMatrix = formattedMatrix.slice(1);
    formattedMatrix = formattedMatrix.slice(0, -1);
    console.log(formattedMatrix);
}

const Board = ({ cells = 12, existingLayout, accessLayout, children, ...props }) => {
    const boardRef = useRef();
    const [firstRun, setFirstRun] = useState(true);
    const [layout, setLayout] = useState(existingLayout.layout ?? {});
    const [cellSize, setCellSize] = useState(null);
    const [numberOfWidgets, setNumberOfWidgets] = useState(children.length);
    const [isDragging, setDragging] = useState(false);

    useLayoutEffect(() => {
        setCellSize(boardRef.current.getBoundingClientRect().width / cells);
    }, [])

    const expandGrid = (grid, ceiling) => {
        while (grid.length - ceiling < 5) {
            grid.push(Array(cells).fill(0));
        }
    };

    const placeHere = (grid, toX, toY, dimensions) => {
        expandGrid(grid, toY + dimensions.y)
        for (let y = toY; y < toY + dimensions.y; y++) {
            for (let x = toX; x < toX + dimensions.x; x++) {
                grid[y][x] = 1;
            }
        }
    };

    const isValid = (grid, toX, toY, dimensions) => {
        expandGrid(grid, toY + dimensions.y)
        for (let y = toY; y < toY + dimensions.y; y++) {
            for (let x = toX; x < toX + dimensions.x; x++) {
                if (!grid[y] || grid[y][x] === 1 || x > (cells - 1)) {
                    return false;
                }
            }
        }
        return true;
    };

    const packGrid = (draggedId, rect) => {
        const sortedTiles = Object.entries(layout)
            .filter(e => children.find(f => f.props.tileId === e[0]))
            .sort((a, b) => {
                a = a[1];
                b = b[1];

                const posA = a.position;
                const posB = b.position;

                if (posA.y < posB.y) return -1;
                if (posA.y > posB.y) return 1;

                if (posA.x < posB.x) return -1;
                if (posA.x > posB.x) return 1;

                return 0;
            });

        const grid = Array(cells).fill().map(() => Array(cells).fill(0));

        sortedTiles
            .filter(([id, tile]) => tile.pinned)
            .forEach(([id, tile]) => {
                placeHere(
                    grid,
                    id !== draggedId ? tile.position.x : rect.position.x,
                    id !== draggedId ? tile.position.y : rect.position.y,
                    id !== draggedId ? tile.dimensions : rect.dimensions,
                );
            });

        if (draggedId) {
            placeHere(grid, rect.position.x, rect.position.y, rect.dimensions);
            // stringifyAndLogMatrix(grid)
        }

        sortedTiles.forEach(([id, tile]) => {
            if (id !== draggedId && !tile.pinned) {
                const dimensions = tile.dimensions;
                let toX = 0;
                let toY = 0;

                while (!isValid(grid, toX, toY, dimensions)) {
                    if (toX < cells) {
                        toX++;
                    } else {
                        toX = 0;
                        toY++;
                    }
                }

                placeHere(grid, toX, toY, dimensions);
                shiftLayout(id, { x: toX, y: toY });
                // console.log('PLACING', id)
                // stringifyAndLogMatrix(grid)
            }
        });
    };

    const shiftLayout = (id, position) => {
        if (shallowCompare(layout[id].position, position)) return;
        setLayout((e) => {
            return {
                ...e,
                [id]: {
                    ...e[id],
                    position: position,
                }
            }
        })
    }

    const updateLayout = (id, position, dimensions, pinned = false, fontSize = 0.8) => {
        setLayout((e) => {
            return {
                ...e,
                [id]: {
                    position,
                    dimensions,
                    pinned,
                    fontSize
                }
            }
        })
        if (isDragging)
            packGrid(id, { position: position, dimensions: dimensions });
    }

    useEffect(() => {
        if (Object.entries(layout).length && firstRun) {
            setFirstRun(false);
            packGrid();
        }
        if (!firstRun && accessLayout) {
            accessLayout(layout);
        }
        // console.log(layout)
    }, [layout])

    useEffect(() => {
        if (children.length !== numberOfWidgets) {
            packGrid();
        }
        setNumberOfWidgets(children.length);
    }, [children])

    useEffect(() => {
        const element = boardRef.current;

        const resizeObserver = new ResizeObserver(() => {
            if (!boardRef.current) return;
            setCellSize(boardRef.current.getBoundingClientRect().width / cells);
        });

        if (element) {
            resizeObserver.observe(element);
        }

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (Object.entries(layout).length && !firstRun) {
            if (isDragging) return;
            setLayout(e => {
                return {
                    ...e,
                    ...existingLayout.layout
                }
            })
        }
    }, [existingLayout.id]);

    const tilePack = {
        updateLayout,
        layout,
        cells,
        cellSize,
        boardRef
    }

    const childrenWithProps = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, { tilePack: tilePack });
        }
        return child;
    });

    return (
        <Wrapper
            id='Dashboard'
            ref={boardRef}
            onMouseDown={() => setDragging(true)}
            onMouseUp={() => setDragging(false)}
            {...props}
        >
            {cellSize && childrenWithProps}
        </Wrapper>
    );
}

export default Board;
